import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllUserNotes } from '../api/notes';
import pb from '../pocketbase';
import NotesSkeleton from '../components/NotesSkeleton/NotesSkeleton';
import { usePageCache } from '../hooks/usePageCache';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator/PullToRefreshIndicator';
import './MyNotesPage.css';
import './SavedPage.css';
import NoteDetailOverlay from '../components/NoteDetailOverlay/NoteDetailOverlay';
import { applyGender, getCachedGender } from '../utils/genderUtils';

export default function MyNotesPage() {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isDeepLinked, setIsDeepLinked] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Обработка навигации с параметром для открытия конкретной заметки
    useEffect(() => {
        if (!isLoading && notes.length > 0 && location.state?.openNoteCardId) {
            const targetNote = notes.find(n =>
                n.cardId === location.state.openNoteCardId ||
                (n.card_id === location.state.openNoteCardId)
            );

            if (targetNote) {
                setSelectedNote(targetNote);
                setIsDeepLinked(true);
                window.history.replaceState({}, document.title);
            }
        }
    }, [isLoading, notes, location.state]);
    const { getCachedData } = usePageCache();

    // Используем кэш для загрузки заметок
    const cachedData = getCachedData('my-notes', getAllUserNotes, []);

    console.log('[MyNotesPage] Render. Notes:', cachedData.data?.length, 'Loading:', cachedData.loading);

    // Обновляем состояние из кэша при первой загрузке
    useEffect(() => {
        console.log('[MyNotesPage] useEffect cachedData.data changed:', cachedData.data?.length);
        if (cachedData.data) {
            setNotes(cachedData.data);
            if (isInitialLoad) {
                setIsLoading(false);
                setIsInitialLoad(false);
            }
        } else if (!cachedData.loading && isInitialLoad) {
            // Если данных нет и загрузка закончилась (например, ошибка или пустой список)
            setIsLoading(false);
            setIsInitialLoad(false);
        }
    }, [cachedData.data, isInitialLoad, cachedData.loading]);

    // Debug: проверяем структуру данных
    useEffect(() => {
        if (notes.length > 0) {
            console.log('[MyNotesPage] Sample note data:', notes[0]);
        }
    }, [notes]);

    // Обновляем состояние загрузки из кэша
    useEffect(() => {
        if (cachedData.loading !== undefined) {
            setIsLoading(cachedData.loading);
        }
    }, [cachedData.loading]);

    // Функция для pull-to-refresh
    const handleRefresh = useCallback(async () => {
        await cachedData.refresh();
    }, [cachedData]);

    // Настройка pull-to-refresh
    const {
        elementRef,
        isRefreshing,
        isPulling,
        pullDistance,
        refreshProgress,
        canRefresh
    } = usePullToRefresh(handleRefresh, {
        threshold: 80,
        resistance: 2.5,
        maxPullDistance: 120,
        enabled: !isLoading
    });

    const [viewMode, setViewMode] = useState('all'); // 'games' | 'all'
    const [selectedGame, setSelectedGame] = useState(null);

    // ... (existing useEffects)

    const getNotesByGames = () => {
        const gamesMap = new Map();
        notes.forEach(note => {
            if (!gamesMap.has(note.gameId)) {
                gamesMap.set(note.gameId, {
                    id: note.gameId,
                    title: note.gameTitle,
                    subtitle: note.gameSubtitle,
                    cover_path: note.gameCoverPath,
                    count: 0
                });
            }
            gamesMap.get(note.gameId).count++;
        });
        return Array.from(gamesMap.values()).sort((a, b) => b.count - a.count);
    };

    const getGroupedNotes = (notesToGroup) => {
        const groups = {};
        notesToGroup.forEach(note => {
            // Fallback: use updatedAt, then createdAt
            const dateStr = note.updatedAt || note.createdAt;
            const date = dateStr ? new Date(dateStr) : null;

            // Skip if invalid date
            if (!date || isNaN(date.getTime())) {
                console.warn('Invalid date for note:', note.id, dateStr);
                return;
            }

            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let groupKey = '';
            if (date.toDateString() === today.toDateString()) {
                groupKey = 'Сегодня';
            } else if (date.toDateString() === yesterday.toDateString()) {
                groupKey = 'Вчера';
            } else {
                groupKey = date.toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            }

            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(note);
        });
        return Object.entries(groups);
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getThumbUrl = (note) => {
        // Use cardId and cardImagePath from expanded card data
        if (!note.cardId || !note.cardImagePath) return null;
        return `${pb.baseUrl}/api/files/cards/${note.cardId}/${note.cardImagePath}`;
    };

    const getCoverUrl = (note) => {
        if (!note.gameId || !note.gameCoverPath) return '/placeholder-game.jpg';
        return `${pb.baseUrl}/api/files/games/${note.gameId}/${note.gameCoverPath}`;
    };

    const [selectedNote, setSelectedNote] = useState(null);

    const handleNoteClick = (note) => {
        setSelectedNote(note);
    };

    const handleNoteSaved = async () => {
        console.log('[MyNotesPage] handleNoteSaved called. Waiting 500ms...');
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('[MyNotesPage] Refreshing cache...');
        cachedData.refresh();
    };

    const handleCloseNote = () => {
        if (isDeepLinked) {
            navigate('/');
        } else {
            setSelectedNote(null);
        }
        setIsDeepLinked(false);
    };

    const handleGameClick = (game) => {
        setSelectedGame(game);
    };

    const handleBackToGames = () => {
        setSelectedGame(null);
    };

    // Скрываем глобальный tabbar когда выбрана игра
    useEffect(() => {
        if (selectedGame) {
            document.body.classList.add('hide-tabbar');
        } else {
            document.body.classList.remove('hide-tabbar');
        }

        // Cleanup при размонтировании
        return () => {
            document.body.classList.remove('hide-tabbar');
        };
    }, [selectedGame]);

    // Определяем, какие заметки показывать
    const displayedNotes = selectedGame
        ? notes.filter(n => n.gameId === selectedGame.id)
        : notes;

    const groupedNotes = getGroupedNotes(displayedNotes);
    const gamesList = getNotesByGames();

    return (
        <>
            <PullToRefreshIndicator
                isRefreshing={isRefreshing}
                isPulling={isPulling}
                progress={refreshProgress}
                canRefresh={canRefresh}
                pullDistance={pullDistance}
            />
            <main className="page" ref={elementRef}>
                {/* Заголовок меняется в зависимости от режима */}
                {selectedGame ? (
                    <div className="page-header">
                        <h1>{selectedGame.title}</h1>
                    </div>
                ) : (
                    <h1>Мои заметки</h1>
                )}

                {/* Переключатель режимов (скрываем, если выбрана игра) */}
                {!selectedGame && !isLoading && (
                    <div className="saved-mode-switch">
                        <button
                            className={`mode-btn ${viewMode === 'all' ? 'mode-btn--active' : ''}`}
                            onClick={() => setViewMode('all')}
                        >
                            Все
                        </button>
                        <button
                            className={`mode-btn ${viewMode === 'games' ? 'mode-btn--active' : ''}`}
                            onClick={() => { }}
                        // onClick={() => setViewMode('games')}
                        >
                            По играм
                        </button>
                    </div>
                )}

                {isLoading ? (
                    <NotesSkeleton count={3} />
                ) : notes.length === 0 ? (
                    <div className="saved-empty-state">
                        <h3>У вас пока нет заметок</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>Записывайте ответы на вопросы во время игры, чтобы прочесть их в будущем</p>
                    </div>
                ) : (
                    <>
                        {/* Список игр */}
                        {viewMode === 'games' && !selectedGame && (
                            <div className="saved-games-list">
                                {gamesList.map(game => (
                                    <div
                                        key={game.id}
                                        className="game-card"
                                        onClick={() => handleGameClick(game)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <img
                                            src={getCoverUrl(game.id)}
                                            alt={game.title}
                                            className="game-cover"
                                        />
                                        <div className="game-meta">
                                            <div className="game-title">{game.title}</div>
                                            <div className="game-subtitle">
                                                {game.subtitle}
                                            </div>
                                            <div className="saved-game-count">
                                                <img src="/icons/note-white.svg" alt="" />
                                                <span>{game.count}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Список заметок (для "Все" или выбранной игры) */}
                        {(viewMode === 'all' || selectedGame) && (
                            <div className="notes-list">
                                {groupedNotes.map(([dateGroup, groupNotes]) => (
                                    <div key={dateGroup} className="notes-group">
                                        <div className="notes-date-header">{dateGroup}</div>
                                        {groupNotes.map(note => (
                                            <div key={note.id} className="note-item" onClick={() => handleNoteClick(note)}>
                                                <div className="note-thumb-container">
                                                    <img
                                                        src={getThumbUrl(note) || '/placeholder-card.png'}
                                                        alt=""
                                                        className="note-thumb"
                                                        onError={(e) => {
                                                            e.target.src = '/placeholder-card.png';
                                                            e.target.style.backgroundColor = '#f0f0f0';
                                                        }}
                                                    />
                                                </div>
                                                <div className="note-info">
                                                    <div className="note-top-row">
                                                        <div className="note-question-text">{note.gameTitle}</div>
                                                        <span className="note-time">{formatTime(note.updatedAt)}</span>
                                                    </div>
                                                    <div className="note-top-row">
                                                        <div className="note-question-text">{applyGender(note.cardQuestion, getCachedGender())}</div>
                                                    </div>
                                                    <div className="note-text-preview">
                                                        {note.content}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Custom TabBar for Game View */}
            {selectedGame && (
                <div className="tabbar-wrap" style={{ justifyContent: 'flex-start', zIndex: 200 }}>
                    <button
                        className="tabbar single"
                        onClick={handleBackToGames}
                        aria-label="Назад"
                    >
                        <img src="/icons/previous.svg" alt="Назад" />
                    </button>
                </div>
            )}

            {/* Overlay с деталями заметки */}
            {selectedNote && (
                <NoteDetailOverlay
                    note={selectedNote}
                    imageUrl={getThumbUrl(selectedNote)}
                    onClose={handleCloseNote}
                    onNoteSaved={handleNoteSaved}
                />
            )}
        </>
    );
}
